import { useEffect, useState } from "react";
import { useViewAsClient } from "@/lib/viewAsClient";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Copy, Globe, Link as LinkIcon, Plus, Trash2 } from "lucide-react";
import {
  deletePublicLink,
  listPublicLinks,
  listSitesLite,
  upsertPublicLink,
} from "@/lib/api";

export default function PublicLinks() {
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);
  const { viewAsClientId } = useViewAsClient();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [r, s] = await Promise.all([listPublicLinks(), listSitesLite()]);
      if (cancelled) return;
      if (r.error) toast.error(r.error.message);
      let links = r.data || [];
      if (viewAsClientId) links = links.filter((l) => l.client_id === viewAsClientId);
      setRows(links);
      setSites(s.data || []);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, tick]);

  const baseUrl = window.location.origin;
  const copyUrl = async (slug) => {
    const url = `${baseUrl}/book/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error(url);
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete public link "${row.title}"?`)) return;
    const { error } = await deletePublicLink(row.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted."); refresh(); }
  };

  const toggleActive = async (row) => {
    const { error } = await upsertPublicLink({ id: row.id, is_active: !row.is_active });
    if (error) toast.error(error.message);
    else refresh();
  };

  return (
    <div data-testid="public-links-page" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Globe className="h-6 w-6" /> Public booking links
          </h1>
          <p className="text-sm text-slate-500">
            Share a slug-based form with brokers / customers — they can submit
            bookings without logging in. Each link is scoped to one site.
          </p>
        </div>
        <Button onClick={() => setEditing({ __new: true })} data-testid="link-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New link
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-sm text-slate-400">
                  No links yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`link-row-${r.id}`}>
                  <TableCell>
                    <div className="font-medium">{r.title}</div>
                    {r.description && (
                      <div className="text-xs text-slate-500 line-clamp-1">{r.description}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.site?.name || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                        /book/{r.slug}
                      </code>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => copyUrl(r.slug)}
                        data-testid={`link-copy-${r.id}`}
                        title="Copy full URL"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!r.is_active}
                        onCheckedChange={() => toggleActive(r)}
                        data-testid={`link-toggle-${r.id}`}
                      />
                      <Badge
                        variant="secondary"
                        className={r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}
                      >
                        {r.is_active ? "Live" : "Paused"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(r)}
                        data-testid={`link-edit-${r.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => doDelete(r)}
                        data-testid={`link-delete-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <FormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        link={editing?.__new ? null : editing}
        sites={sites}
        onSaved={refresh}
      />
    </div>
  );
}

function FormDialog({ open, onOpenChange, link, sites, onSaved }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="link-form-dialog">
        <Inner
          key={link?.id || "new"}
          link={link}
          sites={sites}
          onSaved={(row) => { onSaved?.(row); onOpenChange(false); }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function Inner({ link, sites, onSaved, onCancel }) {
  const isEdit = !!link?.id;
  const [v, setV] = useState(() => link || {
    slug: "",
    title: "",
    description: "",
    site_id: "",
    is_active: true,
    requires_approval: true,
    allowed_directions: ["inbound", "outbound"],
    max_bookings_per_day: null,
    advance_booking_days: 14,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const toggleDir = (dir) => {
    const cur = v.allowed_directions || [];
    set("allowed_directions", cur.includes(dir) ? cur.filter((d) => d !== dir) : [...cur, dir]);
  };

  const submit = async () => {
    if (!v.slug || !v.title || !v.site_id) {
      toast.error("Slug, title and site are required.");
      return;
    }
    setBusy(true);
    // derive client_id from chosen site
    const site = sites.find((s) => s.id === v.site_id);
    const { error } = await upsertPublicLink({
      ...v,
      client_id: v.client_id || site?.client_id || null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(isEdit ? "Link updated." : "Link created.");
    onSaved?.();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle data-testid="link-form-title">
          {isEdit ? "Edit public link" : "New public link"}
        </DialogTitle>
        <DialogDescription>
          Shareable booking form. The slug becomes the public URL — keep it
          short and human-readable.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <Field label="Title *">
          <Input value={v.title} onChange={(e) => set("title", e.target.value)} data-testid="link-title" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Slug *">
            <Input
              value={v.slug}
              onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              placeholder="public-intake"
              data-testid="link-slug"
            />
          </Field>
          <Field label="Site *">
            <Select value={v.site_id} onValueChange={(x) => set("site_id", x)}>
              <SelectTrigger data-testid="link-site"><SelectValue placeholder="Pick a site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Description (shown on the form)">
          <Textarea
            rows={2}
            value={v.description || ""}
            onChange={(e) => set("description", e.target.value)}
            data-testid="link-description"
          />
        </Field>
        <div className="flex items-center gap-4">
          {["inbound", "outbound"].map((d) => (
            <label key={d} className="flex items-center gap-2 text-sm">
              <Switch
                checked={(v.allowed_directions || []).includes(d)}
                onCheckedChange={() => toggleDir(d)}
                data-testid={`link-dir-${d}`}
              />
              {d}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Advance booking window (days)">
            <Input
              type="number"
              value={v.advance_booking_days || 14}
              onChange={(e) => set("advance_booking_days", parseInt(e.target.value, 10) || 14)}
              data-testid="link-advance"
            />
          </Field>
          <Field label="Max bookings per day (blank = unlimited)">
            <Input
              type="number"
              value={v.max_bookings_per_day ?? ""}
              onChange={(e) => set("max_bookings_per_day", e.target.value ? parseInt(e.target.value, 10) : null)}
              data-testid="link-max-day"
            />
          </Field>
        </div>
        <div className="flex items-center gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!v.is_active} onCheckedChange={(x) => set("is_active", x)} data-testid="link-active" />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!v.requires_approval} onCheckedChange={(x) => set("requires_approval", x)} data-testid="link-requires-approval" />
            Requires approval
          </label>
        </div>
        <div className="pt-2 text-xs text-slate-500 flex items-center gap-1">
          <LinkIcon className="h-3.5 w-3.5" />
          Public URL preview:&nbsp;
          <code className="font-mono bg-slate-100 px-1 rounded">
            {window.location.origin}/book/{v.slug || "your-slug"}
          </code>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="link-submit">
          {busy ? "Saving…" : isEdit ? "Save changes" : "Create link"}
        </Button>
      </DialogFooter>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
